"use client";

import { useMemo, useEffect, useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import { ArrowLeftCircle, ArrowRightCircle, ChevronRight } from "lucide-react";
import { useSearchParams } from "next/navigation";
import useUrlQuery from "@/hooks/useUrlQuery";
import * as NProgress from "nprogress";
import { useScopedI18n } from "@/messages/client";
import InputNumber from "../blocks/Form/InputNumber";

interface PaginationProps {
  className?: string;
  total: number;
  pageSize?: number;
  current?: number;
  onChange?: (current: number) => void;
  mode?: "server" | "client";
}

export default function Pagination({
  className,
  total,
  pageSize = 10,
  mode = "server",
  current,
  onChange,
}: PaginationProps) {
  const t = useScopedI18n("分页");
  const query = useSearchParams();
  const { changeUrlQuery } = useUrlQuery();
  const currentPage = current ?? Number(query.get("page") ?? "1");
  const [jumpPage, setJumpPage] = useState<number>();

  const [pageRenderArray, setPageRenderArray] = useState<Array<number>>([]);

  const pageChange = useCallback(
    (page: number) => {
      if (page > 1) {
        if (mode === "server") {
          changeUrlQuery("page", String(page));
        }
        onChange?.(page);
      } else {
        if (mode === "server") {
          changeUrlQuery("page", null);
        }
        onChange?.(1);
      }
    },
    [changeUrlQuery, onChange]
  );

  const totalPage = useMemo(() => {
    const num = Math.ceil(total / pageSize);
    if (num >= 7) {
      setPageRenderArray([1, 2, 3, 4, 5]);
    } else {
      if (num >= 2) {
        const array = new Array(num as number).fill(0);
        array.forEach((_, index) => {
          array[index] = index + 1;
        });
        setPageRenderArray(array);
      } else {
        setPageRenderArray([]);
      }
    }
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  //点击改页码
  const changePage = useCallback(
    (pageNum: number) => {
      //小型分页器
      if (totalPage <= 6) {
        pageChange(pageNum);
        // return setNowIndex(pageNum)
        return;
      }
      if (pageNum > 4 && pageNum <= totalPage - 4) {
        setPageRenderArray([
          pageNum - 2,
          pageNum - 1,
          pageNum,
          pageNum + 1,
          pageNum + 2,
        ]);
      }
      //页码为1的情况
      if (pageNum <= 4) {
        setPageRenderArray([1, 2, 3, 4, 5]);
      }
      //页码到倒数第四页内的情况
      if (pageNum > totalPage - 4) {
        setPageRenderArray([
          totalPage - 4,
          totalPage - 3,
          totalPage - 2,
          totalPage - 1,
          totalPage,
        ]);
      }
      // setNowIndex(pageNum)
      pageChange(pageNum);
    },
    [pageChange, totalPage]
  );

  const handlePrevious = useCallback(() => {
    if (currentPage > 1) {
      changePage(currentPage - 1);
    }
  }, [currentPage, changePage]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPage) {
      changePage(currentPage + 1);
    }
  }, [currentPage, totalPage, changePage]);

  // useEffect(() => {
  //   if (
  //     (current && current > 0 && totalPage && current <= totalPage) ||
  //     (currentPage && currentPage > 0 && totalPage && currentPage <= totalPage)
  //   ) {
  //     changePage(current || currentPage);
  //     NProgress.done();
  //   }
  // }, [changePage, current, currentPage, totalPage]);

  useEffect(() => {
    onChange?.(currentPage);
  }, [currentPage, onChange]);

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center gap-y-3 md:flex-row",
        className
      )}>
      <div className="flex select-none justify-center">
        <span
          className={cn(
            " flex h-8 w-8 items-center justify-center text-gray transition-all duration-300",
            {
              "cursor-pointer hover:text-secondary": currentPage > 1,
              "cursor-not-allowed opacity-30": currentPage === 1,
            }
          )}
          onClick={handlePrevious}>
          <ArrowLeftCircle />
        </span>
        {/** 第1页始终显示，如果分页器与第一页中间有其他页（即当前页>=5） 显示省略号 */}
        {pageRenderArray.length > 0 && !pageRenderArray.includes(1) && (
          <>
            <span
              className="mx-1 h-8 w-8 cursor-pointer text-center text-sm leading-8 text-gray hover:text-secondary"
              onClick={() => changePage(1)}>
              1
            </span>
            {currentPage >= 5 && (
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center text-gray"
                )}>
                ···
              </span>
            )}
          </>
        )}

        {pageRenderArray.length ? (
          pageRenderArray.map((i) => (
            <span
              key={i}
              className={cn("mx-1 h-8 w-8 text-center text-sm leading-8", {
                "cursor-default text-primary": currentPage === i,
                "cursor-pointer text-gray hover:text-secondary":
                  currentPage !== i,
              })}
              onClick={() => changePage(i)}>
              {i}
            </span>
          ))
        ) : (
          <span
            className={cn(
              "mx-1 h-8 w-8 cursor-default text-center text-sm leading-8 text-gray"
            )}>
            1
          </span>
        )}
        {/** 末页始终显示，如果分页器与末页中间有其他页（即当前页<末页-3） 显示省略号 */}
        {pageRenderArray.length > 0 && !pageRenderArray.includes(totalPage) && (
          <>
            {currentPage < totalPage - 3 && (
              <span
                className={cn(
                  "flex h-8 w-8 items-center justify-center text-gray"
                )}>
                ···
              </span>
            )}
            <span
              className="mx-1 h-8 w-8 cursor-pointer text-center text-sm leading-8 text-gray hover:text-secondary"
              onClick={() => changePage(totalPage)}>
              {totalPage}
            </span>
          </>
        )}
        <span
          className={cn(
            "flex h-8 w-8 items-center justify-center text-gray transition-all duration-300",
            {
              "cursor-pointer hover:text-secondary": currentPage < totalPage,
              "cursor-not-allowed opacity-30": currentPage === totalPage,
            }
          )}
          onClick={handleNext}>
          <ArrowRightCircle />
        </span>
      </div>
      <div className="ml-6 flex items-center text-gray">
        {t("前往第")}
        <InputNumber
          className="mx-2 h-6 w-12"
          field={{ value: jumpPage, onChange: setJumpPage }}
          min={1}
          max={totalPage}
          stepper={false}
        />
        {t("页")}
        <span
          className={cn(
            "ml-5 flex cursor-pointer items-center hover:text-primary",
            {
              "pointer-events-none":
                !jumpPage || Number(jumpPage) === currentPage,
            }
          )}
          onClick={() => changePage(Number(jumpPage || 1))}>
          {t("跳转")}
          <ChevronRight
            className={cn({
              "text-primary": jumpPage && Number(jumpPage) !== currentPage,
            })}
          />
        </span>
      </div>
    </div>
  );
}
